import { Icon } from '@iconify/react/dist/iconify.js';
import axios from "axios";
import { ArcElement, Chart, Legend, Tooltip } from 'chart.js';
import { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useAuth } from '../core/AuthContext';
import { URL } from '../core/config';
import NavHeader from "./NavHeader";
import Avatar from "./Avatar";
import { canViewOpenCallResults, canParticipateInOpenCallVoting } from '../core/utils/permissionUtils';
import permissionService from '../core/services/PermissionService';
import "../styles/CompareVolunteers.css";
import "../styles/general.css";
import "../styles/main.css";

import "../styles/OpenCallResults.css";

// Register the required chart components
Chart.register(ArcElement, Tooltip, Legend);

const usersURL = URL + "/users";
const openCallsURL = URL + "/opencall";

const OpenCallResults = () => {
  const authContext = useAuth();
  const contextUser = authContext.user;

  const location = useLocation();
  const history = useHistory();
  const goBack = () => history.push(location.state?.from || '/opencalls-list');
  const [sardines, setSardines] = useState([]);
  const [voters, setVoters] = useState([]);
  const [openCall, setOpenCall] = useState(null);
  const [loading, setLoading] = useState(true);



  // Check if user has permission to view this page
  const canViewResults = canViewOpenCallResults(contextUser);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const opencallId = params.get('opencall');

    const fetchOpenCall = async (openCallId) => {
      try {
        const response = await axios.get(`${openCallsURL}/${openCallId}`);
        const openCall = response.data;
        const sardineIds = openCall.shots.map(shot => shot.user);

        if (sardineIds.length === 0) {
          setLoading(false);
          return;
        }

        const promises = sardineIds.map(id =>
          axios.get(`${usersURL}/${id}`)
            .then(response => response.data)
        );

        const sardinesData = await Promise.all(promises);
        const sardinesWithComments = sardinesData.map((sardine) => {
          const shot = openCall.shots.find((s) => String(s.user) === String(sardine._id));
          return {
            ...sardine,
            openCallComment: shot?.comment || "",
          };
        });

        setOpenCall(openCall);
        setSardines(sardinesWithComments);

        setLoading(false);
      } catch (error) {
        console.error('Error fetching sardines data:', error);
        setLoading(false);
      }
    };

     const fetchVoters = async () => {
       try {
         const [usersResponse, permissionsResponse] = await Promise.all([
           axios.get(usersURL + "/list"),
           permissionService.getAllPermissions().catch(() => []),
         ]);
         const openCallPermissions = (permissionsResponse || []).filter((permission) =>
           ["VOTE_OPENCALL", "MANAGE_OPENCALLS"].includes(permission.action_key),
         );
         const voters = usersResponse.data.filter((user) =>
           canParticipateInOpenCallVoting(user, openCallPermissions),
         );
         setVoters(voters);
       } catch (err) {
         setVoters([]);
       }
     };

    fetchOpenCall(opencallId);
    fetchVoters();
  }, [location]);

  // -----------------------------

  if (loading) {
    return <div className="page-container">Loading sardines data...</div>;
  }

  // Permission check - must be done after loading user context
   if (!loading && !canViewResults) {
     return (
       <div className="page-container">
         <NavHeader />
         <div style={{ marginTop: '40px' }}>
           <h1 className='page-title'>Access Denied</h1>
           <div style={{ textAlign: 'center', marginTop: '20px' }}>
             <p>You do not have permission to view this page.</p>
             <div style={{ marginTop: '30px' }}>
               <p>Your details:</p>
               <ul style={{ listStyle: 'none', padding: 0 }}>
                 <li>Email: {contextUser.email || 'Email not available'}</li>
                 <li>Name: {contextUser.fullName || contextUser.name || 'Name not available'}</li>
                 <li>Position: {contextUser.position || 'Position not available'}</li>
               </ul>
             </div>
             <div className="button-container" style={{ marginTop: '30px' }}>
               <button
                 className="button back-button"
                 onClick={goBack}
               >
                 Go Back
               </button>
             </div>
           </div>
         </div>
       </div>
     );
   }

  if (sardines.length === 0) {
    return (
      <div className="page-container">
        <h1 className='page-title'>There are no sardines for this Open Call</h1>
      </div>
    );
  }

  return (
    <div className="page-container">
      <NavHeader />

      <h1 className='page-title'>Open Call Votes</h1>

      <div className="vote-instructions">Number of Spots: {openCall.numberSpots}</div>

      {openCall.winners?.length > 0 && (
        <div className="opencall-winners-section">
          <h2 className="opencall-winners-title">
            <Icon icon="mdi:prize" width={22} height={22} style={{ marginRight: 6, verticalAlign: "middle" }} />
            Winners
          </h2>
          <ul className="opencall-winners-list">
            {openCall.winners.map((winnerId) => {
              const sardine = sardines.find((s) => String(s._id) === String(winnerId));
              const comment = openCall.shots.find((s) => String(s.user) === String(winnerId))?.comment;
              const name = sardine?.fullName || sardine?.name || "Unknown";
              return (
                <li key={winnerId} className="opencall-winner-item">
                  <Icon icon="tabler:fish" width={16} height={16} style={{ marginRight: 4 }} />
                  <span className="opencall-winner-name">{name}</span>
                  {comment && <span className="opencall-winner-shift">{comment}</span>}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <div className="opencall-votes-table-container">
        <table className="opencall-votes-table">
          <thead>
            <tr>
              <th className='root-cell'>Voter \ Sardine</th>
              {sardines.map(sardine => (
                <th key={sardine._id}>
                  <div className='users-shots'>
                    <Avatar user={sardine} size={32} />
                    <span className="sardine-cell-name">{sardine.fullName || sardine.name}</span>
                    {sardine.openCallComment && (
                      <span className="sardine-shift-comment">{sardine.openCallComment}</span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {voters.map(voter => (
              <tr key={voter._id}>
                <td className="voter-cell">
                  <Avatar user={voter} size={32} />
                  <span className="voter-cell-name">{voter.fullName || voter.name}</span>
                </td>
                {sardines.map(sardine => {
                  const voted = openCall?.votersVotes
                    && openCall.votersVotes[String(voter._id)]
                    && openCall.votersVotes[String(voter._id)].some(id => String(id) === String(sardine._id));
                  return (
                    <td key={sardine._id} className={voted ? "voted-cell" : ""}>
                      {voted && (
                        <Icon icon="mdi:check-bold" color="#10b981" width={22} height={22} />
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="button-container">
        <button
          className="button back-button"
          onClick={goBack}
        >
          Go Back
        </button>
      </div>
    </div>
  );
};

export default OpenCallResults;
